// ============================================================================
// Agent Chat APIs
//
// GraphQL calls for the AutoMate agent:
//   - `sendAgentMessage`  — mutation that dispatches a user question and
//                            returns a conversationId + sessionId
//   - `onAgentResponse`   — subscription that streams response chunks back
//
// Both calls go through the direct AppSync client in `./AISearchAppSync`,
// which talks to the dedicated agent AppSync API without using Amplify's
// global config. See that file's header for the rationale.
//
// The subscription payload is re-shaped to match Amplify's traditional
// subscription envelope (`{ value: { data: ... } }`) so React callers keep
// a single familiar response shape.
// ============================================================================

import { aiGraphqlMutation, aiGraphqlSubscribe } from 'src/utilities/AISearchAppSync';

/**
 * Send a user message to the agent.
 *
 * @param {string} message    – user's question
 * @param {string|null} sessionId – optional conversation session to continue.
 *                                  Pass null to start a fresh conversation.
 * @param {(result: any) => void} onSuccess – called with the AppSync response
 *      `{ data: { sendAgentMessage: { conversationId, sessionId } } }`.
 * @param {(err: any) => void}    onFailure – called if the mutation errors
 *      out at transport level or returns GraphQL errors.
 */
export const sendAgentMessage = async (message, sessionId, onSuccess, onFailure) => {
  const query = `mutation MyMutation($input: SendAgentMessageInput!) {
    sendAgentMessage(input: $input) {
      conversationId
      sessionId
    }
  }`;
  const variables = {
    input: { message, ...(sessionId && { sessionId }) },
  };
  try {
    const result = await aiGraphqlMutation(query, variables);
    if (result?.errors) {
      if (onFailure) onFailure(result);
      return;
    }
    if (onSuccess) onSuccess(result);
  } catch (error) {
    if (onFailure) onFailure(error);
  }
};

/**
 * Subscribe to streamed response chunks for a conversation.
 *
 * @param {string} conversationId – id returned by `sendAgentMessage`
 * @param {(response: { value: { data: { onAgentResponse: object } } }) => void} onResult
 *      – fired for each `data` frame received. The outer `value` wrapper keeps
 *        the shape compatible with Amplify's subscription envelope.
 * @param {(err: any) => void} onFailure – called on protocol / transport error
 *
 * @returns {Promise<{ unsubscribe: () => void }>} — capture this and call
 *      `.unsubscribe()` when the component unmounts or a new subscription
 *      starts, so the underlying WebSocket is torn down cleanly.
 */
export const onAgentResponse = async (conversationId, onResult, onFailure) => {
  const query = `subscription MySubscription($conversationId: String!) {
    onAgentResponse(conversationId: $conversationId) {
      conversationId
      sessionId
      chunkIndex
      content
      status
    }
  }`;
  try {
    return await aiGraphqlSubscribe(
      query,
      { conversationId },
      (payload) => onResult({ value: payload }),
      onFailure
    );
  } catch (error) {
    if (onFailure) onFailure(error);
    return null;
  }
};
